import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { meetingAPI, bodyAPI, agendaAPI, attendanceAPI, userAPI } from '../api';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { format, parseISO } from 'date-fns';
import { 
  ArrowLeft, Calendar, Clock, MapPin, Video, Users, 
  FileText, Play, Edit, Loader2, CheckCircle, AlertCircle,
  Plus, Send, MoreVertical, Copy, ShieldCheck, Truck, MessageSquare, Save,
  Sparkles, Download, FileDown, Share2
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import MeetingChecklist from '../components/MeetingChecklist';
import { toast } from 'sonner';

const MeetingDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [meeting, setMeeting] = useState(null);
  const [body, setBody] = useState(null);
  const [agendaItems, setAgendaItems] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logistics, setLogistics] = useState({});
  const [logisticsNotes, setLogisticsNotes] = useState('');
  const [savingLogistics, setSavingLogistics] = useState(false);
  const [summaryHtml, setSummaryHtml] = useState(null);
  const [summaryGeneratedAt, setSummaryGeneratedAt] = useState(null);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [downloadingSummary, setDownloadingSummary] = useState(null);
  const [summaryShared, setSummaryShared] = useState(false);
  const [togglingShare, setTogglingShare] = useState(false);

  const LOGISTICS_ITEMS = [
    { key: 'venue_confirmed', label: 'Venue confirmed and reserved' },
    { key: 'av_equipment', label: 'AV equipment checked' },
    { key: 'projector_screen', label: 'Projector and screen ready' },
    { key: 'name_cards', label: 'Name cards prepared' },
    { key: 'printers_ready', label: 'Printers available and working' },
    { key: 'papers_printed', label: 'Papers and handouts printed' },
    { key: 'pens_supplies', label: 'Pens and writing supplies' },
    { key: 'menu_catering', label: 'Menu / catering arranged' },
    { key: 'shuttle_transport', label: 'Shuttle / transportation arranged' },
    { key: 'parking_space', label: 'Parking space arranged' },
    { key: 'other_arrangements', label: 'Other arrangements' },
  ];

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [meetingRes, agendaRes, attendanceRes, usersRes] = await Promise.all([
        meetingAPI.get(id),
        agendaAPI.getByMeeting(id),
        attendanceAPI.getByMeeting(id),
        userAPI.getAll(),
      ]);

      setMeeting(meetingRes.data);
      setAgendaItems(agendaRes.data);
      setAttendance(attendanceRes.data);
      setUsers(usersRes.data);
      setLogistics(meetingRes.data.logistics || {});
      setLogisticsNotes(meetingRes.data.logistics?.notes || '');

      // Fetch existing summary
      if (meetingRes.data.summary_html) {
        setSummaryHtml(meetingRes.data.summary_html);
        setSummaryGeneratedAt(meetingRes.data.summary_generated_at);
        setSummaryShared(!!meetingRes.data.summary_shared_to_portal);
      } else {
        try {
          const summaryRes = await meetingAPI.getSummary(id);
          if (summaryRes.data?.summary_html) {
            setSummaryHtml(summaryRes.data.summary_html);
            setSummaryGeneratedAt(summaryRes.data.generated_at);
          }
        } catch (e) { /* No summary yet */ }
      }

      // Fetch body details
      if (meetingRes.data.body_id) {
        const bodyRes = await bodyAPI.get(meetingRes.data.body_id);
        setBody(bodyRes.data);
      }
    } catch (err) {
      console.error('Failed to fetch meeting details:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-amber-100 text-amber-800 border-amber-300',
      scheduled: 'bg-green-100 text-green-800 border-green-300',
      completed: 'bg-blue-100 text-blue-800 border-blue-300',
      cancelled: 'bg-red-100 text-red-800 border-red-300',
      rescheduled: 'bg-purple-100 text-purple-800 border-purple-300',
    };
    return colors[status] || 'bg-gray-100 text-gray-700 border-gray-300';
  };

  const updateStatus = async (newStatus) => {
    try {
      await meetingAPI.update(id, { status: newStatus });
      setMeeting({ ...meeting, status: newStatus });
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user?.full_name || 'Unknown';
  };

  const toggleLogisticsItem = (key) => {
    setLogistics(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const saveLogistics = async () => {
    setSavingLogistics(true);
    try {
      const data = { ...logistics, notes: logisticsNotes };
      await meetingAPI.update(id, { logistics: data });
      setMeeting({ ...meeting, logistics: data });
      toast.success('Logistics saved');
    } catch (err) {
      toast.error('Failed to save logistics');
    } finally {
      setSavingLogistics(false);
    }
  };

  // Quorum calculation
  const quorumConfig = body?.quorum_config || { type: 'percentage', value: 50 };
  const totalMembers = body?.members?.length || users.length;
  const quorumRequired = quorumConfig.type === 'percentage'
    ? Math.ceil(totalMembers * (quorumConfig.value / 100))
    : quorumConfig.value;
  const confirmedCount = attendance.filter(a =>
    a.status === 'present_onsite' || a.status === 'present_virtual'
  ).length;
  const completedLogistics = LOGISTICS_ITEMS.filter(i => logistics[i.key]).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-[#00A3A3]" />
      </div>
    );
  }

  if (!meeting) {
    return (
      <div className="text-center py-16">
        <AlertCircle className="h-12 w-12 mx-auto mb-4 text-red-500" />
        <h2 className="text-xl font-semibold mb-2">Meeting not found</h2>
        <Link to="/meetings">
          <Button variant="outline">Back to Meetings</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="meeting-detail-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div>
          <Button 
            variant="ghost" 
            onClick={() => navigate('/meetings')}
            className="mb-4 text-[#5A6570] hover:text-[#1A1D21] -ml-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Meetings
          </Button>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-2xl font-bold text-[#1A1D21]">{meeting.title}</h1>
            <span className={`text-xs px-2 py-1 rounded border font-medium ${getStatusColor(meeting.status)}`}>
              {meeting.status.replace('_', ' ').toUpperCase()}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-4 text-sm text-[#5A6570]">
            <span className="flex items-center gap-1.5">
              <Calendar className="h-4 w-4" />
              {format(parseISO(meeting.date_time), 'EEEE, MMMM d, yyyy')}
            </span>
            <span className="flex items-center gap-1.5">
              <Clock className="h-4 w-4" />
              {format(parseISO(meeting.date_time), 'h:mm a')}
            </span>
            {body && (
              <span className="flex items-center gap-1.5">
                <Users className="h-4 w-4" />
                {body.name}
              </span>
            )}
          </div>
        </div>

        <div className="flex gap-3">
          {meeting.status === 'draft' && (
            <Button 
              onClick={() => updateStatus('scheduled')}
              className="bg-[#00A3A3] hover:bg-[#008585]"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Confirm Schedule
            </Button>
          )}
          {(meeting.status === 'notice_sent' || meeting.status === 'scheduled') && (
            <Button 
              onClick={() => navigate(`/meetings/${id}/console`)}
              className="bg-[#F58A26] hover:bg-[#D67520]"
              data-testid="document-meeting-btn"
            >
              <Play className="h-4 w-4 mr-2" />
              Document Meeting
            </Button>
          )}
        </div>
      </div>

      {/* Meeting Info Cards */}
      <div className="grid md:grid-cols-3 gap-4">
        {meeting.venue && (
          <Card className="border-[#E2E6EA]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#00A3A3]/10 rounded-lg">
                  <MapPin className="h-5 w-5 text-[#00A3A3]" />
                </div>
                <div>
                  <p className="text-xs text-[#5A6570]">Venue</p>
                  <p className="font-medium text-[#1A1D21]">{meeting.venue}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        {meeting.virtual_link && (
          <Card className="border-[#E2E6EA]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#F58A26]/10 rounded-lg">
                  <Video className="h-5 w-5 text-[#F58A26]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-[#5A6570]">Virtual Link</p>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(meeting.virtual_link);
                      toast.success('Link copied to clipboard');
                    }}
                    className="font-medium text-[#00A3A3] hover:underline block"
                    data-testid="copy-virtual-link-btn"
                  >
                    Copy Meeting Link
                  </button>
                  <a 
                    href={meeting.virtual_link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="font-medium text-[#1A1D21] hover:text-[#00A3A3] hover:underline block"
                  >
                    Join Meeting
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        <Card className="border-[#E2E6EA]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#00A3A3]/10 rounded-lg">
                <FileText className="h-5 w-5 text-[#00A3A3]" />
              </div>
              <div>
                <p className="text-xs text-[#5A6570]">Basis</p>
                <p className="font-medium text-[#1A1D21] capitalize">{meeting.basis} Meeting</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Meeting Checklist */}
      <MeetingChecklist
        meeting={meeting}
        body={body}
        agendaItems={agendaItems}
        attendance={attendance}
        onNavigate={(target) => {
          switch (target) {
            case 'agenda':
              navigate(`/meetings/${id}/agenda`);
              break;
            case 'console':
              navigate(`/meetings/${id}/console`);
              break;
            case 'minutes':
              navigate(`/minutes?meeting_id=${id}`);
              break;
            case 'records':
              navigate('/records');
              break;
            case 'admin':
              navigate('/admin');
              break;
            default:
              break;
          }
        }}
      />

      {/* Tabs */}
      <Tabs defaultValue="agenda" className="space-y-4">
        <TabsList className="tabs-grid-4 bg-[#F0F2F4] p-1">
          <TabsTrigger value="agenda" className="data-[state=active]:bg-white text-xs sm:text-sm" data-testid="tab-provisional-agenda">
            Provisional Agenda
          </TabsTrigger>
          <TabsTrigger value="quorum" className="data-[state=active]:bg-white text-xs sm:text-sm" data-testid="tab-quorum">
            Quorum Confirmation
          </TabsTrigger>
          <TabsTrigger value="logistics" className="data-[state=active]:bg-white text-xs sm:text-sm" data-testid="tab-logistics">
            Logistics
          </TabsTrigger>
          <TabsTrigger value="summary" className="data-[state=active]:bg-white text-xs sm:text-sm" data-testid="tab-summary">
            Summary
          </TabsTrigger>
        </TabsList>

        {/* 1) Provisional Agenda */}
        <TabsContent value="agenda">
          <Card className="border-[#E2E6EA]">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Provisional Agenda</CardTitle>
              <Button 
                onClick={() => navigate(`/meetings/${id}/agenda`)}
                className="bg-[#00A3A3] hover:bg-[#008585]"
                data-testid="edit-agenda-btn"
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit Agenda
              </Button>
            </CardHeader>
            <CardContent>
              {agendaItems.length === 0 ? (
                <div className="text-center py-8 text-[#5A6570]">
                  <FileText className="h-10 w-10 mx-auto mb-3 text-[#A8B1B9]" />
                  <p>No agenda items yet</p>
                  <Button 
                    variant="link" 
                    className="text-[#00A3A3] mt-2"
                    onClick={() => navigate(`/meetings/${id}/agenda`)}
                  >
                    Add agenda items
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {agendaItems.map((item, index) => (
                    <div 
                      key={item.id}
                      className="flex items-start gap-4 p-4 rounded-lg border border-[#E2E6EA] hover:border-[#00A3A3] transition-colors"
                      data-testid={`agenda-item-${index}`}
                    >
                      <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#00A3A3]/10 text-[#00A3A3] flex items-center justify-center font-semibold text-sm">
                        {index + 1}
                      </span>
                      <div className="flex-1">
                        <h4 className="font-medium text-[#1A1D21]">{item.title}</h4>
                        <div className="flex items-center gap-3 mt-1 text-sm text-[#5A6570]">
                          <span className="px-2 py-0.5 rounded bg-[#F0F2F4] text-xs">
                            {item.item_type.replace('_', ' ')}
                          </span>
                          {item.presenter && (
                            <span>Presenter: {getUserName(item.presenter)}</span>
                          )}
                          {item.estimated_minutes && (
                            <span>{item.estimated_minutes} min</span>
                          )}
                        </div>
                        {item.description && (
                          <p className="mt-2 text-sm text-[#5A6570]">{item.description}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* 2) Quorum Confirmation */}
        <TabsContent value="quorum">
          <Card className="border-[#E2E6EA]">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-[#00A3A3]" />
                Quorum Confirmation
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className={`p-4 rounded-lg border ${confirmedCount >= quorumRequired ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                <div className="flex items-center gap-3">
                  {confirmedCount >= quorumRequired
                    ? <CheckCircle className="h-6 w-6 text-green-600" />
                    : <AlertCircle className="h-6 w-6 text-amber-600" />
                  }
                  <div>
                    <p className={`font-semibold ${confirmedCount >= quorumRequired ? 'text-green-800' : 'text-amber-800'}`}>
                      {confirmedCount >= quorumRequired ? 'Quorum Can Be Achieved' : 'Quorum Not Yet Met'}
                    </p>
                    <p className="text-sm text-[#5A6570]">
                      {confirmedCount} confirmed of {quorumRequired} required ({totalMembers} total members) &mdash;
                      {quorumConfig.type === 'percentage' ? ` ${quorumConfig.value}% quorum rule` : ` ${quorumConfig.value} fixed quorum`}
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-[#1A1D21] mb-2">
                  {body?.name || 'Organization'} Members
                </p>
                <div className="space-y-2">
                  {(body?.members || users).map((member) => {
                    const userId = member.user_id || member.id;
                    const userName = member.full_name || getUserName(userId);
                    const role = member.role || member.position || '';
                    const record = attendance.find(a => a.user_id === userId);
                    const status = record?.status || 'not_confirmed';
                    const isPresent = status === 'present_onsite' || status === 'present_virtual';

                    return (
                      <div key={userId}
                        className="flex items-center justify-between p-3 rounded-lg border border-[#E2E6EA]"
                        data-testid={`quorum-member-${userId}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isPresent ? 'bg-green-100' : 'bg-gray-100'}`}>
                            <Users className={`h-4 w-4 ${isPresent ? 'text-green-600' : 'text-gray-400'}`} />
                          </div>
                          <div>
                            <p className="font-medium text-sm text-[#1A1D21]">{userName}</p>
                            {role && <p className="text-xs text-[#5A6570] capitalize">{role.replace('_', ' ')}</p>}
                          </div>
                        </div>
                        <Badge className={`text-xs ${
                          isPresent ? 'bg-green-100 text-green-800' :
                          status === 'absent' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {isPresent ? 'Confirmed' : status === 'absent' ? 'Absent' : 'Pending'}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              </div>

              <p className="text-xs text-[#A8B1B9]">
                Attendance will be finalized in the Live Console during the actual meeting.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* 3) Logistics */}
        <TabsContent value="logistics">
          <Card className="border-[#E2E6EA]">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Truck className="h-5 w-5 text-[#F58A26]" />
                  Logistics for the Meeting
                </CardTitle>
                <p className="text-xs text-[#5A6570] mt-1">{completedLogistics} of {LOGISTICS_ITEMS.length} items checked</p>
              </div>
              <Button onClick={saveLogistics} disabled={savingLogistics} className="bg-[#00A3A3] hover:bg-[#008585]" data-testid="save-logistics-btn">
                {savingLogistics ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save
              </Button>
            </CardHeader>
            <CardContent className="space-y-3">
              {LOGISTICS_ITEMS.map((item) => (
                <label key={item.key}
                  className="flex items-center gap-3 p-3 rounded-lg border border-[#E2E6EA] hover:border-[#00A3A3]/50 cursor-pointer transition-colors"
                  data-testid={`logistics-${item.key}`}
                >
                  <Checkbox
                    checked={!!logistics[item.key]}
                    onCheckedChange={() => toggleLogisticsItem(item.key)}
                  />
                  <span className={`text-sm ${logistics[item.key] ? 'text-[#1A1D21] line-through opacity-60' : 'text-[#1A1D21]'}`}>
                    {item.label}
                  </span>
                </label>
              ))}

              <div className="pt-2">
                <p className="text-sm font-medium text-[#1A1D21] mb-1">Additional Notes</p>
                <textarea
                  value={logisticsNotes}
                  onChange={(e) => setLogisticsNotes(e.target.value)}
                  placeholder="Any additional logistics notes (e.g., dietary restrictions, special requirements)..."
                  className="w-full border border-[#E2E6EA] rounded-md px-3 py-2 text-sm min-h-[80px] focus:outline-none focus:ring-2 focus:ring-[#00A3A3]/20"
                  data-testid="logistics-notes"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* 4) Summary of Discussions and Agreements */}
        <TabsContent value="summary">
          <Card className="border-[#E2E6EA]">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-[#00A3A3]" />
                  Summary of Discussions and Agreements
                </CardTitle>
                <div className="flex items-center gap-2">
                  {summaryHtml && (
                    <>
                      <Button
                        variant="outline" size="sm"
                        onClick={async () => {
                          setDownloadingSummary('pdf');
                          try {
                            const res = await meetingAPI.downloadSummary(id, 'pdf');
                            const url = window.URL.createObjectURL(new Blob([res.data]));
                            const link = document.createElement('a');
                            link.href = url;
                            link.setAttribute('download', `${meeting.title?.replace(/\s+/g, '_')}_Summary.pdf`);
                            document.body.appendChild(link);
                            link.click();
                            link.remove();
                            window.URL.revokeObjectURL(url);
                            toast.success('PDF downloaded');
                          } catch (e) { toast.error('Failed to download PDF'); }
                          finally { setDownloadingSummary(null); }
                        }}
                        disabled={downloadingSummary === 'pdf'}
                        data-testid="download-summary-pdf"
                      >
                        {downloadingSummary === 'pdf' ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <FileDown className="h-4 w-4 mr-1" />}
                        PDF
                      </Button>
                      <Button
                        variant="outline" size="sm"
                        onClick={async () => {
                          setDownloadingSummary('docx');
                          try {
                            const res = await meetingAPI.downloadSummary(id, 'docx');
                            const url = window.URL.createObjectURL(new Blob([res.data]));
                            const link = document.createElement('a');
                            link.href = url;
                            link.setAttribute('download', `${meeting.title?.replace(/\s+/g, '_')}_Summary.docx`);
                            document.body.appendChild(link);
                            link.click();
                            link.remove();
                            window.URL.revokeObjectURL(url);
                            toast.success('DOCX downloaded');
                          } catch (e) { toast.error('Failed to download DOCX'); }
                          finally { setDownloadingSummary(null); }
                        }}
                        disabled={downloadingSummary === 'docx'}
                        data-testid="download-summary-docx"
                      >
                        {downloadingSummary === 'docx' ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Download className="h-4 w-4 mr-1" />}
                        DOCX
                      </Button>
                    </>
                  )}
                  {summaryHtml && (
                    <Button
                      variant={summaryShared ? "default" : "outline"}
                      size="sm"
                      onClick={async () => {
                        setTogglingShare(true);
                        try {
                          const res = await meetingAPI.toggleSummaryShare(id);
                          setSummaryShared(res.data.shared);
                          toast.success(res.data.shared ? 'Summary shared to Stakeholders Portal' : 'Summary removed from Stakeholders Portal');
                        } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update sharing'); }
                        finally { setTogglingShare(false); }
                      }}
                      disabled={togglingShare}
                      className={summaryShared ? 'bg-[#00A3A3] hover:bg-[#008585] text-white' : ''}
                      data-testid="share-summary-portal-btn"
                    >
                      {togglingShare ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Share2 className="h-4 w-4 mr-1" />}
                      {summaryShared ? 'Shared' : 'Share to Portal'}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    onClick={async () => {
                      setGeneratingSummary(true);
                      try {
                        const res = await meetingAPI.generateSummary(id);
                        setSummaryHtml(res.data.summary_html);
                        setSummaryGeneratedAt(res.data.generated_at);
                        toast.success('Summary generated');
                      } catch (e) {
                        toast.error(e.response?.data?.detail || 'Failed to generate summary');
                      } finally { setGeneratingSummary(false); }
                    }}
                    disabled={generatingSummary}
                    className="bg-[#00A3A3] hover:bg-[#008585]"
                    data-testid="generate-summary-btn"
                  >
                    {generatingSummary ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Sparkles className="h-4 w-4 mr-1" />}
                    {summaryHtml ? 'Regenerate' : 'Generate Summary'}
                  </Button>
                </div>
              </div>
              {summaryGeneratedAt && (
                <p className="text-xs text-[#A8B1B9] mt-1">Generated: {format(parseISO(summaryGeneratedAt), 'MMM d, yyyy h:mm a')}</p>
              )}
            </CardHeader>
            <CardContent>
              {generatingSummary ? (
                <div className="text-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-[#00A3A3] mx-auto mb-3" />
                  <p className="text-sm text-[#5A6570]">Generating AI summary from discussion points...</p>
                  <p className="text-xs text-[#A8B1B9] mt-1">This may take a moment</p>
                </div>
              ) : summaryHtml ? (
                <div
                  className="prose prose-sm max-w-none text-[#1A1D21] [&_h1]:text-lg [&_h1]:font-bold [&_h1]:text-[#1A1D21] [&_h2]:text-base [&_h2]:font-semibold [&_h3]:text-sm [&_h3]:font-semibold [&_ul]:list-disc [&_ul]:ml-4 [&_ol]:list-decimal [&_ol]:ml-4 [&_p]:text-sm [&_p]:leading-relaxed [&_li]:text-sm"
                  dangerouslySetInnerHTML={{ __html: summaryHtml }}
                  data-testid="summary-content"
                />
              ) : (
                <div className="text-center py-8 text-[#5A6570]">
                  <MessageSquare className="h-10 w-10 mx-auto mb-3 text-[#A8B1B9]" />
                  <p className="text-sm">No summary generated yet.</p>
                  <p className="text-xs mt-1 text-[#A8B1B9]">Click "Generate Summary" to create an AI-powered summary from the meeting's discussion points.</p>
                  <p className="text-xs mt-1 text-[#A8B1B9]">Ensure discussion points are recorded in the Live Console first.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MeetingDetail;
